import { subclass, declared, property } from "@arcgis/core/core/accessorSupport/decorators";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";
import { VNode } from 'src/interfaces/interfaces';

import t9nAppMessages from "../../t9n/App/App.json";
import ContentAreaViewModel from '../ui/ContentArea/ContentAreaViewModel';
import "./DeleteModal/DeleteModal.scss";

export interface DeleteModalProperties extends __esri.WidgetProperties {
  appInfo: __esri.PortalItem;
  orgUrl: string;
  contentAreaVM: ContentAreaViewModel;
}

@subclass("Landing.widgets.DeleteModal")
export default class DeleteModal extends declared(Widget) {

  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  appInfo: __esri.PortalItem;

  @property()
  orgUrl: string;

  @property()
  contentAreaVM: ContentAreaViewModel;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  @property()
  readonly modalVisible: boolean = false;
  
  private _canDelete: boolean;
  private _isDeleting: boolean = false;
  private _isQueryingForCanDelete: boolean;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  constructor(props: DeleteModalProperties) {
    super(props);
  }

  render() {
    const { close, deleteLabel } = this.appMessages.myApps.delete;
    return (
      <calcite-modal
        active={this.modalVisible}
        color="red"
        background-color="white"
        scale="m"
        width="s"
        intl-close={close}
        afterCreate={(modal: HTMLCalciteModalElement)=>{
          modal.addEventListener("calciteModalClose", ()=>{
            this.hideModal();
          });
        }}
      >
        <h1 slot="header">{deleteLabel}</h1>
        {
          ...this._renderModalContent()
        }
      </calcite-modal>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------
  showModal(): void {
    this._set("modalVisible", true);
    this._isQueryingForCanDelete = true;
    this.scheduleRender();
    this._queryForCanDelete().then(() => {
      setTimeout(()=>{ // very slight delay to ease the calcite-loader out
        this._isQueryingForCanDelete = false;
        this.scheduleRender();
      }, 700);
    });
  }

  hideModal(): void {
    this._set("modalVisible", false);
  }

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _renderModalContent(): VNode[] {
    const { cancel, deleteLabel } = this.appMessages.myApps.delete;
    if (this._isQueryingForCanDelete) {
      return [
        <calcite-loader slot="content" active={true}></calcite-loader>,
        <calcite-button
          key="delete_cancelBtn_fake"
          slot="secondary"
          class="placeholder-button"
        > {cancel}
        </calcite-button>,
        <calcite-button
          key="delete_deleteBtn_fake"
          slot="primary"
          class="placeholder-button"
        >{deleteLabel}
        </calcite-button>
      ]
    } else {
      return [
        !this._canDelete
          ? this._renderDeleteProtection()
          : this._renderDeleteConfirmation(),

        <calcite-button
          key="delete_cancelBtn"
          slot="secondary"
          width="full"
          appearance="outline"
          color="red"
          alignment="center"
          scale="m"
          onclick={this.hideModal.bind(this)}
        >
          {cancel}
        </calcite-button>,

        this._canDelete && (
          <calcite-button
            key="delete_deleteBtn"
            slot="primary"
            width="full"
            appearance="solid"
            color="red"
            alignment="center"
            scale="m"
            loading={this._isDeleting}
            onclick={this._performDelete.bind(this)}
          >
            { deleteLabel}
          </calcite-button>
        )
      ]
    }
  }

  private async _queryForCanDelete() {
    const deleteCheckRes = await this.contentAreaVM.publishAppVM.checkCanDelete(this.appInfo.id)
    this._canDelete = deleteCheckRes.success;
  }

  private _renderDeleteProtection() {
    const { deleteProtectionMsg } = this.appMessages.myApps.delete;
    const { id, title } = this?.appInfo;

    return (
      <div slot="content" key="canDeleteFalse">
        <p> {deleteProtectionMsg} </p>
        <p>
          <calcite-link
            icon-start="launch"
            href={`${this.orgUrl}/home/item.html?id=${id}#settings`}
            target="_blank"
          >
            {title}
          </calcite-link>
        </p>
      </div>
    )
  }

  private _renderDeleteConfirmation() {
    const { deleteConfirmMsg } = this.appMessages.myApps.delete;
    return (
      <div slot="content" key="canDeleteTrue">
        <p>
          {deleteConfirmMsg}
        </p>
      </div>
    )
  }

  private _performDelete(): void {
    const { deleteSuccess, deleteFailed } = this.appMessages.myApps.delete;
    const { id, title } = this.appInfo;

    this._isDeleting = true;
    this.scheduleRender();
    this.contentAreaVM.publishAppVM.deleteItem(id).then((deleteRes) => {
      this._isDeleting = false;
      this.hideModal();
      this.scheduleRender();
      if (deleteRes === true) {
        this.contentAreaVM.showAlert(
          deleteSuccess.title,
          `"${title}" ${deleteSuccess.msg}`,
          "success"
        );
        this.contentAreaVM.myAppsDataVM.removeFromMyAppsOnDelete(id);

      } else {
        this.contentAreaVM.showAlert(
          deleteFailed.title,
          `${deleteFailed.msg1}: ${title} ${deleteFailed.msg2}. ${deleteRes !== false && deleteRes}`,
          "error"
        );
      }
    });
  }


}